export interface NextActionOption {
  id: string;
  name: string;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

export interface CPE {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  plant_type: PlantType[];
  internal_positioning: InternalPositioning | null;
  cpe_status: CPEStatus | null;
  power: number | null;
  short_description: string | null;
  notes: string | null;
  company_cui: string | null;
  epc: string | null;
  dispatcher: string | null;
  maintenance_company: string | null;
  project_link: string | null;
  internal_link: string | null;
  exact_location: boolean;
  locality: string | null;
  county: string | null;
  is_waldevar_pp: boolean;
  commercial_status: CommercialStatus | null;
  om_account_manager: string | null;
  energization_year: number | null;
  image_url: string | null;
  zone_id: string | null;
  next_action_id: string | null;
  next_action_due_date: string | null;
  created_by: string;
  updated_by: string;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date | null;
  creator?: {
    id: string;
    email: string;
    name: string;
  };
  updater?: {
    id: string;
    email: string;
    name: string;
  };
  zone?: {
    id: string;
    name: string;
  };
  accountManager?: {
    id: string;
    email: string;
    name: string;
  };
  nextAction?: NextActionOption | null;
}

export type PlantType = 'PV' | 'BESS' | 'Gas' | 'Hydro' | 'Floating';

export type InternalPositioning = 'go' | 'no_go';

export type CPEStatus =
  | 'Project'
  | 'Design'
  | 'Ready to Build'
  | 'Execution'
  | 'Testing'
  | 'Commissioning (PIF)'
  | 'Completed'
  | 'Energized';

export type CommercialStatus =
  | 'Uncontacted'
  | 'Contacted'
  | 'O&M Offered'
  | 'O&M Contracted'
  | 'Lost O&M';

export interface CPEFormData {
  name: string;
  latitude: number | string;
  longitude: number | string;
  plant_type: PlantType[];
  internal_positioning: InternalPositioning | null;
  cpe_status: CPEStatus | null;
  power: number | string | null;
  short_description: string;
  notes: string;
  company_cui: string;
  epc: string;
  dispatcher: string;
  maintenance_company: string;
  project_link: string;
  internal_link: string;
  exact_location: boolean;
  is_waldevar_pp: boolean;
  commercial_status: CommercialStatus | null;
  om_account_manager: string | null;
  energization_year: number | string | null;
  zone_id: string | null;
  next_action_id: string | null;
  next_action_due_date: string | null;
}

export interface CPEFilters {
  search?: string;
  plant_type?: PlantType[];
  cpe_status?: CPEStatus[];
  commercial_status?: CommercialStatus[];
  internal_positioning?: InternalPositioning[];
  energization_year?: number;
  min_power?: number;
  max_power?: number;
  om_account_manager?: string | string[];
  zone_id?: string | string[];
  include_deleted?: boolean;
  is_waldevar_pp?: boolean;
  updated_from?: string;
  updated_to?: string;
  next_action_id?: string; // specific UUID, 'none' for unset
  overdue_next_action?: boolean;
}

export interface CPEPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface CPEListResponse {
  success: boolean;
  data: {
    cpes: CPE[];
    pagination: CPEPagination;
  };
}

export interface CPEResponse {
  success: boolean;
  data: {
    cpe: CPE;
  };
}

export interface CPEAuditLog {
  id: string;
  cpe_id: string;
  user_id: string;
  action: 'create' | 'update' | 'delete' | 'restore';
  changed_fields: Record<string, unknown> | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  created_at: Date;
  user?: {
    id: string;
    email: string;
    name: string;
  };
}

export interface CPEAuditLogResponse {
  success: boolean;
  data: {
    auditLogs: CPEAuditLog[];
  };
}

export interface ValidateLocationResponse {
  success: boolean;
  data: {
    exists: boolean;
    cpe: CPE | null;
  };
}

// Constants
export const PLANT_TYPES: PlantType[] = ['PV', 'BESS', 'Gas', 'Hydro', 'Floating'];

export const CPE_STATUSES: CPEStatus[] = [
  'Project',
  'Design',
  'Ready to Build',
  'Execution',
  'Testing',
  'Commissioning (PIF)',
  'Completed',
  'Energized',
];

export const COMMERCIAL_STATUSES: CommercialStatus[] = [
  'Uncontacted',
  'Contacted',
  'O&M Offered',
  'O&M Contracted',
  'Lost O&M',
];

export const INTERNAL_POSITIONING_OPTIONS: InternalPositioning[] = ['go', 'no_go'];

export const PAGE_SIZE_OPTIONS = [20, 50, 100];
